// src/pages/PricingPage.jsx

import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";

import {
  FiBarChart2,
  FiCheck,
  FiMapPin,
  FiPackage,
  FiTrendingUp,
} from "react-icons/fi";

import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";

import PublicLayout from "../components/PublicLayout";

const BRAND_RED = "#E63946";
const BRAND_RED_DARK = "#C92A37";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

// -------------------------------------------------------
// PRICING PAGE
// -------------------------------------------------------

export default function PricingPage() {
  return (
    <PublicLayout>
      <Helmet>
        <title>
          Pricing | Postcode.Coupons
        </title>

        <meta
          name="description"
          content="Postcode.Coupons pricing. Place coupons in postcode sectors for 1p per household per sector and upgrade to Pro for QR analytics."
        />

        <link
          rel="canonical"
          href="https://postcode.coupons/pricing"
        />

        <meta
          property="og:title"
          content="Postcode.Coupons Pricing"
        />

        <meta
          property="og:description"
          content="Coupon placement from 1p per household per postcode sector, with optional Pro QR analytics."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <Box
        bg="white"
        minH="100vh"
      >
        {/* HERO */}

        <Box
          as="section"
          pt={{
            base: 10,
            md: 16,
          }}
          pb={{
            base: 8,
            md: 12,
          }}
          bg="white"
        >
          <Container maxW="container.lg">
            <VStack
              spacing={4}
              textAlign="center"
              maxW="760px"
              mx="auto"
            >
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg={BRAND_RED_LIGHT}
                color={BRAND_RED_DARK}
                fontSize="xs"
                fontWeight="extrabold"
                letterSpacing="wide"
              >
                POSTCODE.COUPONS PRICING
              </Badge>

              <Heading
                as="h1"
                color="gray.900"
                fontSize={{
                  base: "3xl",
                  md: "5xl",
                }}
                lineHeight="1.08"
                letterSpacing="-0.03em"
              >
                Simple coupon pricing
              </Heading>

              <Text
                maxW="680px"
                color="gray.600"
                fontSize={{
                  base: "md",
                  md: "xl",
                }}
                lineHeight="1.7"
              >
                Place your coupon in postcode sectors for
                1p per household per sector.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* MAIN PRICING */}

        <Box
          as="section"
          py={{
            base: 8,
            md: 12,
          }}
          bg={BRAND_RED_LIGHT}
          borderTopWidth="1px"
          borderBottomWidth="1px"
          borderColor={BRAND_BORDER}
        >
          <Container maxW="container.lg">
            <SimpleGrid
              columns={{
                base: 1,
                md: 2,
              }}
              spacing={{
                base: 5,
                md: 7,
              }}
              alignItems="stretch"
            >
              {/* COUPON PLACEMENT */}

              <PricingCard
                icon={FiMapPin}
                eyebrow="Coupon placement"
                title="1p per household"
                suffix="per postcode sector"
                featured
              >
                <Text
                  color="gray.600"
                  lineHeight="1.7"
                  mb={5}
                >
                  Select a postcode sector and the household
                  count determines the coupon-placement price.
                </Text>

                <List
                  spacing={3}
                  color="gray.700"
                  fontSize="sm"
                >
                  <Feature>
                    Coupon placement in your selected postcode sector
                  </Feature>

                  <Feature>
                    Online coupon presence
                  </Feature>

                  <Feature>
                    QR-enabled coupon
                  </Feature>

                  <Feature>
                    Campaign printing when required capacity is filled
                  </Feature>

                  <Feature>
                    Household distribution through Royal Mail
                  </Feature>
                </List>

                <Button
                  as={RouterLink}
                  to="/coupon-ads"
                  mt={7}
                  width="100%"
                  size="lg"
                  bg={BRAND_RED}
                  color="white"
                  borderRadius="lg"
                  fontWeight="extrabold"
                  _hover={{
                    bg: BRAND_RED_DARK,
                    textDecoration: "none",
                  }}
                >
                  Place Your Coupon
                </Button>
              </PricingCard>

              {/* PRO */}

              <PricingCard
                icon={FiBarChart2}
                eyebrow="Optional upgrade"
                title="£19"
                suffix="per month"
              >
                <Text
                  color="gray.600"
                  lineHeight="1.7"
                  mb={5}
                >
                  Upgrade to Pro when you want deeper QR
                  performance reporting across your coupons.
                </Text>

                <List
                  spacing={3}
                  color="gray.700"
                  fontSize="sm"
                >
                  <Feature>
                    QR analytics dashboard
                  </Feature>

                  <Feature>
                    Coupon impressions
                  </Feature>

                  <Feature>
                    QR scans
                  </Feature>

                  <Feature>
                    Scan-rate reporting
                  </Feature>

                  <Feature>
                    Slot-level performance
                  </Feature>

                  <Feature>
                    Scan trends over time
                  </Feature>

                  <Feature>
                    CSV and PDF reporting
                  </Feature>
                </List>

                <Button
                  as={RouterLink}
                  to="/dashboard/qr-analytics"
                  mt={7}
                  width="100%"
                  size="lg"
                  variant="outline"
                  borderWidth="2px"
                  borderColor={BRAND_RED}
                  color={BRAND_RED_DARK}
                  borderRadius="lg"
                  fontWeight="extrabold"
                  _hover={{
                    bg: BRAND_RED_LIGHT,
                    textDecoration: "none",
                  }}
                >
                  View QR Analytics
                </Button>

                <Text
                  mt={3}
                  color="gray.500"
                  fontSize="xs"
                  textAlign="center"
                >
                  Pro upgrades are completed securely through
                  your business account.
                </Text>
              </PricingCard>
            </SimpleGrid>
          </Container>
        </Box>

        {/* HOW COUPON PRICING WORKS */}

        <Box
          as="section"
          py={{
            base: 10,
            md: 16,
          }}
          bg="white"
        >
          <Container maxW="container.lg">
            <Box
              maxW="720px"
              mx="auto"
              textAlign="center"
              mb={{
                base: 7,
                md: 10,
              }}
            >
              <Heading
                size={{
                  base: "lg",
                  md: "xl",
                }}
                color="gray.900"
              >
                How coupon pricing works
              </Heading>

              <Text
                mt={3}
                color="gray.600"
                lineHeight="1.7"
              >
                The price follows the household count in the
                postcode sector you select.
              </Text>
            </Box>

            <SimpleGrid
              columns={{
                base: 1,
                md: 3,
              }}
              spacing={4}
            >
              <Step
                icon={FiMapPin}
                number="01"
                title="Choose a sector"
              >
                Select the postcode sector where you want your
                coupon placed.
              </Step>

              <Step
                icon={FiPackage}
                number="02"
                title="Households set the price"
              >
                Coupon placement is calculated at 1p for each
                household in that postcode sector.
              </Step>

              <Step
                icon={FiTrendingUp}
                number="03"
                title="Campaign fills"
              >
                The campaign proceeds to printing when the
                required coupon capacity has been filled.
              </Step>
            </SimpleGrid>
          </Container>
        </Box>

        {/* BOTTOM CTA */}

        <Box
          as="section"
          py={{
            base: 10,
            md: 14,
          }}
          bg={BRAND_RED}
          color="white"
        >
          <Container maxW="container.md">
            <VStack
              spacing={4}
              textAlign="center"
            >
              <Heading
                size={{
                  base: "lg",
                  md: "xl",
                }}
              >
                Choose your postcode sector
              </Heading>

              <Text
                maxW="600px"
                opacity={0.94}
                lineHeight="1.7"
              >
                Find the sector you want and see the household
                count before placing your coupon.
              </Text>

              <Button
                as={RouterLink}
                to="/map"
                size="lg"
                mt={2}
                px={8}
                bg="white"
                color={BRAND_RED_DARK}
                borderRadius="lg"
                fontWeight="extrabold"
                _hover={{
                  bg: BRAND_RED_LIGHT,
                  textDecoration: "none",
                }}
              >
                View Postcode Map
              </Button>
            </VStack>
          </Container>
        </Box>
      </Box>
    </PublicLayout>
  );
}

// -------------------------------------------------------
// PRICING CARD
// -------------------------------------------------------

function PricingCard({
  icon: Icon,
  eyebrow,
  title,
  suffix,
  featured = false,
  children,
}) {
  return (
    <Box
      position="relative"
      p={{
        base: 5,
        md: 7,
      }}
      bg="white"
      borderWidth={featured ? "2px" : "1px"}
      borderColor={
        featured
          ? BRAND_RED
          : BRAND_BORDER
      }
      borderRadius="2xl"
      boxShadow={
        featured
          ? "lg"
          : "sm"
      }
    >
      {featured && (
        <Badge
          position="absolute"
          top="-12px"
          right={{
            base: 4,
            md: 6,
          }}
          px={3}
          py={1}
          borderRadius="full"
          bg={BRAND_RED}
          color="white"
          fontSize="10px"
          fontWeight="extrabold"
        >
          COUPON PLACEMENT
        </Badge>
      )}

      <Flex
        align="center"
        gap={3}
        mb={5}
      >
        <Flex
          width="44px"
          height="44px"
          align="center"
          justify="center"
          flexShrink={0}
          borderRadius="full"
          bg={BRAND_RED_LIGHT}
          color={BRAND_RED}
          fontSize="xl"
        >
          <Icon />
        </Flex>

        <Text
          color={BRAND_RED_DARK}
          fontSize="xs"
          fontWeight="extrabold"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {eyebrow}
        </Text>
      </Flex>

      <Heading
        color="gray.900"
        fontSize={{
          base: "3xl",
          md: "4xl",
        }}
        lineHeight="1"
      >
        {title}
      </Heading>

      <Text
        mt={2}
        mb={5}
        color="gray.500"
        fontSize="sm"
        fontWeight="bold"
      >
        {suffix}
      </Text>

      {children}
    </Box>
  );
}

// -------------------------------------------------------
// FEATURE
// -------------------------------------------------------

function Feature({ children }) {
  return (
    <ListItem
      display="flex"
      alignItems="flex-start"
      gap={2}
    >
      <ListIcon
        as={FiCheck}
        color={BRAND_RED}
        mt="3px"
      />

      <Text lineHeight="1.6">
        {children}
      </Text>
    </ListItem>
  );
}

// -------------------------------------------------------
// STEP
// -------------------------------------------------------

function Step({
  icon: Icon,
  number,
  title,
  children,
}) {
  return (
    <Box
      p={{
        base: 5,
        md: 6,
      }}
      borderWidth="1px"
      borderColor={BRAND_BORDER}
      borderRadius="xl"
      bg="white"
      boxShadow="sm"
    >
      <Flex
        align="center"
        justify="space-between"
        mb={4}
      >
        <Flex
          width="42px"
          height="42px"
          align="center"
          justify="center"
          borderRadius="full"
          bg={BRAND_RED_LIGHT}
          color={BRAND_RED}
          fontSize="lg"
        >
          <Icon />
        </Flex>

        <Text
          color={BRAND_RED}
          fontSize="xs"
          fontWeight="extrabold"
          letterSpacing="wide"
        >
          {number}
        </Text>
      </Flex>

      <Heading
        size="sm"
        mb={2}
        color="gray.900"
      >
        {title}
      </Heading>

      <Text
        color="gray.600"
        fontSize="sm"
        lineHeight="1.7"
      >
        {children}
      </Text>
    </Box>
  );
}